import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Timer, Check, Volume2, VolumeX, Play, Pause, RotateCcw, BellRing } from "lucide-react";
import { recipeImage } from "@/constants/brand";

function parseDurationSeconds(text) {
  if (!text) return null;
  const re = /(\d+)\s*(?:-|to|–|—)?\s*(\d+)?\s*(hours?|hrs?|minutes?|mins?|seconds?|secs?)\b/gi;
  const m = re.exec(text);
  if (!m) return null;
  const low = parseInt(m[1], 10);
  const high = m[2] ? parseInt(m[2], 10) : low;
  const val = Math.max(low, high);
  const unit = m[3].toLowerCase();
  if (unit.startsWith("h")) return val * 3600;
  if (unit.startsWith("m")) return val * 60;
  return val;
}

function fmt(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.5, 1].forEach((t) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.frequency.value = 880;
      o.start(ctx.currentTime + t);
      g.gain.setValueAtTime(0.001, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.35);
      o.stop(ctx.currentTime + t + 0.4);
    });
  } catch {}
}

function StepTimer({ seconds }) {
  const [remaining, setRemaining] = useState(seconds);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    setRemaining(seconds);
    setRunning(false);
    setDone(false);
  }, [seconds]);

  useEffect(() => {
    if (!running) return;
    ref.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(ref.current);
          setRunning(false);
          setDone(true);
          beep();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(ref.current);
  }, [running]);

  const reset = () => {
    clearInterval(ref.current);
    setRemaining(seconds);
    setRunning(false);
    setDone(false);
  };

  return (
    <div
      data-testid="step-timer"
      className={`mx-auto mt-8 flex w-full max-w-sm items-center gap-4 rounded-3xl border px-5 py-4 ${
        done ? "border-loretta-red bg-loretta-red/10" : "border-espresso/10 bg-white"
      }`}
    >
      <div className="flex items-center gap-2">
        {done ? <BellRing className="h-6 w-6 text-loretta-red" /> : <Timer className="h-6 w-6 text-loretta-olive" />}
        <span data-testid="step-timer-display" className="font-serif text-3xl font-bold tabular-nums text-espresso">
          {fmt(remaining)}
        </span>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {done ? (
          <span className="text-sm font-semibold text-loretta-red">Time's up!</span>
        ) : (
          <button
            onClick={() => setRunning((r) => !r)}
            data-testid="step-timer-toggle"
            className="flex h-11 items-center gap-1.5 rounded-full bg-loretta-red px-5 text-sm font-semibold text-white transition-colors hover:bg-loretta-redhover"
          >
            {running ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {running ? "Pause" : "Start"}
          </button>
        )}
        <button
          onClick={reset}
          data-testid="step-timer-reset"
          title="Reset timer"
          className="flex h-11 w-11 items-center justify-center rounded-full border border-espresso/15 text-espressolt transition-colors hover:bg-creamalt"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function CookingMode({ recipe, onClose }) {
  const [step, setStep] = useState(0);
  const [voiceOn, setVoiceOn] = useState(false);
  const steps = recipe.steps || [];
  const total = steps.length;
  const isLast = step === total - 1;
  const supportsSpeech = typeof window !== "undefined" && "speechSynthesis" in window;

  const speak = useRef((text) => {});
  speak.current = (text) => {
    if (!supportsSpeech) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.95;
    u.pitch = 1.05;
    window.speechSynthesis.speak(u);
  };

  useEffect(() => {
    if (voiceOn && steps[step]) speak.current(`Step ${step + 1}. ${steps[step]}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, voiceOn]);

  useEffect(() => {
    return () => {
      if (supportsSpeech) window.speechSynthesis.cancel();
    };
  }, [supportsSpeech]);

  const toggleVoice = () => {
    if (voiceOn) {
      window.speechSynthesis.cancel();
      setVoiceOn(false);
    } else {
      setVoiceOn(true);
      speak.current(`Step ${step + 1}. ${steps[step]}`);
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" && !isLast) setStep((s) => s + 1);
      if (e.key === "ArrowLeft" && step > 0) setStep((s) => s - 1);
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, isLast, onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col bg-cream"
      data-testid="cooking-mode"
    >
      <div className="flex items-center justify-between border-b border-espresso/10 px-5 py-4 sm:px-10">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-widest text-loretta-red">Cooking Mode</p>
          <h2 className="truncate font-serif text-xl font-bold text-espresso sm:text-2xl">{recipe.title}</h2>
        </div>
        <div className="flex items-center gap-2">
          {supportsSpeech && (
            <button
              onClick={toggleVoice}
              data-testid="voice-toggle-button"
              title={voiceOn ? "Turn voice off" : "Read steps aloud"}
              className={`flex h-11 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-colors ${
                voiceOn ? "bg-loretta-olive text-cream" : "bg-creamalt text-espresso hover:bg-loretta-accent"
              }`}
            >
              {voiceOn ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
              <span className="hidden sm:inline">{voiceOn ? "Voice on" : "Voice off"}</span>
            </button>
          )}
          <button
            onClick={onClose}
            data-testid="close-cooking-button"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-creamalt text-espresso transition-colors hover:bg-loretta-accent"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="h-1.5 w-full bg-creamalt">
        <div
          className="h-full bg-loretta-red transition-[width] duration-300"
          style={{ width: `${((step + 1) / total) * 100}%` }}
        />
      </div>

      <div className="flex flex-1 flex-col items-center justify-center px-6 py-8 sm:px-10">
        <div className="w-full max-w-3xl text-center">
          <div className="mb-6 flex items-center justify-center gap-3">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-loretta-red font-serif text-2xl font-bold text-white">
              {step + 1}
            </span>
            <span className="text-sm uppercase tracking-widest text-espresso/50">of {total}</span>
          </div>
          <AnimatePresence mode="wait">
            <motion.p
              key={step}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.25 }}
              className="font-serif text-3xl font-medium leading-snug text-espresso sm:text-4xl"
              data-testid="cooking-step-text"
            >
              {steps[step]}
            </motion.p>
          </AnimatePresence>
          {parseDurationSeconds(steps[step]) && (
            <StepTimer key={`t-${step}`} seconds={parseDurationSeconds(steps[step])} />
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 border-t border-espresso/10 px-5 py-5 sm:px-10">
        <button
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          data-testid="prev-step-button"
          className="flex items-center gap-2 rounded-full border border-espresso/15 px-6 py-4 text-lg font-semibold text-espresso transition-colors hover:bg-creamalt disabled:opacity-30"
        >
          <ChevronLeft className="h-5 w-5" /> Back
        </button>
        {isLast ? (
          <button
            onClick={onClose}
            data-testid="finish-cooking-button"
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-loretta-olive px-6 py-4 text-lg font-semibold text-cream transition-colors hover:bg-loretta-olivehover sm:flex-none sm:px-12"
          >
            <Check className="h-5 w-5" /> All Done, Mangia!
          </button>
        ) : (
          <button
            onClick={() => setStep((s) => Math.min(total - 1, s + 1))}
            data-testid="next-step-button"
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-loretta-red px-6 py-4 text-lg font-semibold text-white transition-colors hover:bg-loretta-redhover sm:flex-none sm:px-12"
          >
            Next Step <ChevronRight className="h-5 w-5" />
          </button>
        )}
      </div>
    </motion.div>
  );
}
